import { useEffect, useRef, useState } from 'react';

import isEqual from 'lodash-es/isEqual';
import { AlertTriangleIcon, CopyIcon, HistoryIcon, RefreshCcwIcon, XIcon } from 'lucide-react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { mcpEndpoints } from '@/api/endpoints';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  SidebarWrapper,
  SidebarWrapperContent,
  SidebarWrapperFooter,
  SidebarWrapperForm,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { useResponsiveSidebarClose } from '@/components/organisms/Sidebar/ResponsiveSidebarPanel';
import { Button } from '@/components/ui/button';
import { ToggleButton } from '@/components/ui/custom/ToggleButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { selectBrainId } from '@/redux/session/selectors';
import { checkNameAvailability } from '@/util/checkNames';
import { timeFromNow } from '@/util/dateUtils';

import { HeaderRow } from './HeaderRow';
import { useHeaderVisibility } from './hooks/useHeaderVisibility';
import useMcpServers, { type McpConfig, type McpServer, type McpTool } from './hooks/useMcpServers';
import { getAuthHeaders } from './ResyncDialog';
import { buildHeadersRecord, toMcpTool } from './utils';

type EditMcpServerForm = { name: string; customHeaders: Array<{ key: string; value: string }> };

const getStoredHeaders = (server: McpServer): Record<string, string> => {
  if (server.config && typeof server.config === 'object' && 'headers' in server.config) {
    return server.config.headers ?? {};
  }
  return {};
};

const getInitialHeaders = (server: McpServer): Array<{ key: string; value: string }> => {
  const entries = Object.entries(getStoredHeaders(server));
  return [
    ...entries.map(([key, value]) => ({ key, value: String(value) })),
    { key: '', value: '' },
  ];
};

interface EditMcpServerPanelProps {
  server: McpServer;
  onResync?: (server: McpServer) => void;
}

export function EditMcpServerPanel({ server, onResync }: EditMcpServerPanelProps) {
  const { t } = useTranslation();
  const onClose = useResponsiveSidebarClose();
  const brainId = useSelector(selectBrainId);
  const { updateMcpServer, updateStatus, testMutation } = useMcpServers(brainId ?? undefined);

  const form = useForm<EditMcpServerForm>({
    defaultValues: { name: server.name, customHeaders: getInitialHeaders(server) },
  });

  const {
    control,
    setValue,
    formState: { isDirty },
  } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'customHeaders' });
  const watchedHeaders = useWatch({ control, name: 'customHeaders' });
  const watchedName = useWatch({ control, name: 'name' });

  const [nameError, setNameError] = useState<string | null>(null);
  const [selectedTools, setSelectedTools] = useState<Set<string>>(
    () => new Set(server.tools.map((t) => t.name))
  );

  const headerVisibility = useHeaderVisibility();
  const lastKeyRef = useRef<HTMLInputElement>(null);

  // Fetch fresh tools from server when panel opens or server changes
  useEffect(() => {
    const headers = getAuthHeaders(server);
    testMutation.mutate({ url: server.url, headers });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server.mcp_id]);

  // All available tools from server (fresh fetch)
  const allTools = testMutation.data?.tools ?? [];
  const isLoadingTools = testMutation.isPending;
  const toolsError = testMutation.error;

  const needsResync = server.tools_status === 'needs_sync';

  // Close panel on successful update
  useEffect(() => {
    if (updateStatus === 'success') {
      onClose();
    }
  }, [updateStatus, onClose]);

  const handleSelectAll = () => {
    setSelectedTools(new Set(allTools.map((t) => t.name)));
  };

  const handleSelectNone = () => {
    setSelectedTools(new Set());
  };

  const toggleTool = (toolName: string) => {
    setSelectedTools((prev) => {
      const next = new Set(prev);
      if (next.has(toolName)) {
        next.delete(toolName);
      } else {
        next.add(toolName);
      }
      return next;
    });
  };

  const isUpdating = updateStatus === 'pending';
  // Change detection — compare current state against stored server data
  const storedToolNames = new Set(server.tools.map((t) => t.name));
  const currentHeaders = buildHeadersRecord(watchedHeaders);
  const storedHeaders = getStoredHeaders(server);
  const headersChanged = !isEqual(currentHeaders, storedHeaders);
  const toolsChanged =
    selectedTools.size !== storedToolNames.size ||
    [...selectedTools].some((name) => !storedToolNames.has(name));
  const hasChanges = isDirty || toolsChanged;

  const addHeader = () => {
    append({ key: '', value: '' });
    requestAnimationFrame(() => lastKeyRef.current?.focus());
  };

  const removeHeader = (index: number) => {
    remove(index);
    headerVisibility.shiftAfterRemove(index);
  };

  const handleResync = () => {
    if (!needsResync) {
      toast.info(t('mcp.no_resync_needed'));
      return;
    }
    onResync?.(server);
  };

  const handleUpdate = async () => {
    if (!hasChanges) {
      toast.info(t('mcp.no_changes'));
      return;
    }

    const trimmedName = watchedName.trim();

    // Validate name if changed
    if (trimmedName !== server.name && brainId) {
      try {
        const endpoint = mcpEndpoints.checkName(brainId, trimmedName);
        const { available } = await checkNameAvailability(endpoint);
        if (!available) {
          setNameError(t('validation.name_already_exists'));
          return;
        }
      } catch {
        setNameError(t('validation.error_checking_name'));
        return;
      }
    }

    // Build selected tools from fresh data, fall back to stored tools if server unreachable
    const toolSource = allTools.length > 0 ? allTools : server.tools;
    const selectedMcpTools: McpTool[] = toolSource
      .filter((t) => selectedTools.has(t.name))
      .map(toMcpTool);

    // Preserve OAuth config when present; otherwise send a plain headers-only config
    // (no `auth_type: 'headers'` — that mode no longer exists).
    const nextConfig: McpConfig =
      'auth_type' in server.config && server.config.auth_type === 'oauth'
        ? { ...server.config, headers: currentHeaders }
        : { headers: currentHeaders };

    updateMcpServer({
      brain_id: server.brain_id,
      mcp_id: server.mcp_id,
      name: trimmedName,
      tools: selectedMcpTools,
      ...(headersChanged ? { config: nextConfig } : {}),
    });
  };

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">{t('mcp.edit_mcp_server')}</h2>
        <Button variant="ghost-neutral" size="icon-sm" onClick={onClose}>
          <XIcon size={16} />
        </Button>
      </SidebarWrapperTitle>
      <SidebarWrapperForm autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        <SidebarWrapperContent className="px-1 pb-1">
          {/* Name field */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="mcp-name">{t('mcp.name')}</Label>
            <Input
              id="mcp-name"
              autoFocus
              autoComplete="off"
              value={watchedName}
              onChange={(e) => {
                setValue('name', e.target.value, { shouldDirty: true });
                setNameError(null);
              }}
            />
            {nameError && <p className="text-sm text-destructive">{nameError}</p>}
          </div>

          {/* URL field (read-only with copy) */}
          <div className="flex flex-col gap-2">
            <Label>{t('mcp.server_url')}</Label>
            <div className="flex h-10 items-center gap-2 rounded-md border bg-muted/50 px-3">
              <span className="min-w-0 flex-1 truncate text-sm text-foreground-muted">
                {server.url}
              </span>
              <Button
                type="button"
                variant="ghost-neutral"
                size="icon-sm"
                className="shrink-0"
                onClick={() => {
                  navigator.clipboard.writeText(server.url);
                  toast.success(t('common.copied'));
                }}
              >
                <CopyIcon className="size-4" />
              </Button>
            </div>
          </div>

          {/* Custom headers */}
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-foreground">{t('mcp.custom_headers')}</span>
            {fields.map((field, index) => (
              <HeaderRow
                key={field.id}
                headerKey={watchedHeaders?.[index]?.key ?? ''}
                headerValue={watchedHeaders?.[index]?.value ?? ''}
                isVisible={headerVisibility.visibleHeaders.has(index)}
                isLast={index === fields.length - 1}
                onKeyChange={(val) =>
                  setValue(`customHeaders.${index}.key`, val, { shouldDirty: true })
                }
                onValueChange={(val) =>
                  setValue(`customHeaders.${index}.value`, val, { shouldDirty: true })
                }
                onToggleVisibility={() => headerVisibility.toggle(index)}
                onAdd={addHeader}
                onRemove={() => removeHeader(index)}
                keyRef={index === fields.length - 1 ? lastKeyRef : undefined}
              />
            ))}
          </div>

          {/* Warning banner when resync needed */}
          {needsResync && (
            <div className="text-warning-foreground flex items-center gap-2 rounded-md border border-warning/20 bg-warning/10 p-3 text-sm">
              <AlertTriangleIcon className="size-4 shrink-0" />
              <span>{t('mcp.tools_changed_warning')}</span>
            </div>
          )}

          {/* Available tools */}
          <div className="flex flex-col gap-3 pt-2">
            <div className="flex items-center gap-4">
              <span className="grow text-sm font-medium text-foreground">{t('mcp.tools')}</span>
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <button
                  type="button"
                  className="text-primary disabled:opacity-50"
                  onClick={handleSelectNone}
                  disabled={isLoadingTools || !!toolsError}
                >
                  {t('common.none')}
                </button>
                <span className="text-foreground-muted">·</span>
                <button
                  type="button"
                  className="text-primary disabled:opacity-50"
                  onClick={handleSelectAll}
                  disabled={isLoadingTools || !!toolsError}
                >
                  {t('common.all')}
                </button>
              </div>
            </div>

            {/* Loading state */}
            {isLoadingTools && (
              <div className="flex items-center justify-center py-4">
                <Spinner size="md" />
              </div>
            )}

            {/* Error state */}
            {toolsError && (
              <div className="overflow-hidden rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm wrap-break-word text-destructive">
                {toolsError.message}
              </div>
            )}

            {/* Tool chips */}
            {!isLoadingTools && !toolsError && (
              <div className="scrollbar-hide flex max-h-60 flex-wrap gap-2 overflow-x-hidden overflow-y-auto pb-2">
                {allTools.map((tool) => {
                  const isSelected = selectedTools.has(tool.name);
                  const lastUsedText = server.last_used_at
                    ? timeFromNow(server.last_used_at)
                    : null;
                  return (
                    <InfoTooltip
                      key={tool.name}
                      variant="rich"
                      size="auto"
                      content={
                        <div className="flex max-w-70 min-w-45 flex-col">
                          <div className="flex items-center justify-between gap-2 px-3 py-2">
                            <p className="truncate text-xs leading-4 font-semibold">{tool.name}</p>
                            <div className="flex shrink-0 items-center gap-1 opacity-70">
                              <HistoryIcon className="size-4" />
                              <span className="text-xs">{lastUsedText ?? '-'}</span>
                            </div>
                          </div>
                          {tool.description && (
                            <div className="border-t-[0.5px] border-t-stroke-muted-40 px-3 pt-2.5 pb-3">
                              <p className="line-clamp-4 text-xs leading-5">{tool.description}</p>
                            </div>
                          )}
                        </div>
                      }
                      side="top"
                    >
                      <ToggleButton pressed={isSelected} onClick={() => toggleTool(tool.name)}>
                        {tool.name}
                      </ToggleButton>
                    </InfoTooltip>
                  );
                })}
              </div>
            )}
          </div>
        </SidebarWrapperContent>

        <SidebarWrapperFooter>
          <Button
            type="button"
            disabled={
              !!nameError || needsResync || isLoadingTools || (!!toolsError && !headersChanged)
            }
            isLoading={isUpdating}
            onClick={handleUpdate}
          >
            {t('mcp.update')}
          </Button>
          <Button
            type="button"
            variant="outline-neutral"
            onClick={handleResync}
            disabled={!onResync || isLoadingTools}
          >
            <RefreshCcwIcon className="size-4" />
            {t('mcp.resync')}
          </Button>
        </SidebarWrapperFooter>
      </SidebarWrapperForm>
    </SidebarWrapper>
  );
}
